import type { PublicDisplayOverview, PublicDisplayOverviewStats } from '../../api/displays/overview';

export type DisplayOverviewResponse = {
  success: true;
  stats: PublicDisplayOverviewStats;
  topStates: Array<{ state: string; count: number }>;
};

function isStats(value: unknown): value is PublicDisplayOverviewStats {
  if (!value || typeof value !== 'object') return false;
  const stats = value as Partial<PublicDisplayOverviewStats>;
  return ['total', 'christmas', 'halloween', 'states', 'synchronized', 'featured', 'recentCount']
    .every((key) => Number.isInteger(stats[key as keyof PublicDisplayOverviewStats]) && (stats[key as keyof PublicDisplayOverviewStats] as number) >= 0);
}

export function isDisplayOverviewResponse(value: unknown): value is DisplayOverviewResponse {
  if (!value || typeof value !== 'object') return false;
  const response = value as Partial<DisplayOverviewResponse>;
  return response.success === true
    && isStats(response.stats)
    && Array.isArray(response.topStates)
    && response.topStates.every((state) => (
      Boolean(state)
      && typeof state === 'object'
      && typeof state.state === 'string'
      && Number.isInteger(state.count)
      && state.count >= 0
    ));
}

export async function fetchDisplayOverview(signal?: AbortSignal): Promise<DisplayOverviewResponse> {
  const response = await fetch('/api/displays/overview', { signal });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok || !isDisplayOverviewResponse(body)) {
    throw new Error('Display overview unavailable');
  }
  return body;
}

export type { PublicDisplayOverview };

/** Top US states (slugs, most displays first) from the cached footer read; null when unavailable. */
export async function fetchFooterStates(): Promise<string[] | null> {
  const response = await fetch('/api/displays/overview?footerStates=1');
  const body = (await response.json().catch(() => null)) as { footerStates?: unknown } | null;
  if (!response.ok || !Array.isArray(body?.footerStates)) return null;
  return body.footerStates
    .map((entry) => (entry as { state?: unknown } | null)?.state)
    .filter((state): state is string => typeof state === 'string');
}
